/**
 * GuapoMascot Component
 * =====================
 * Single source of truth for animating the Guapo mascot across the app.
 *
 * Modes:
 *  - "idle":        gentle float / breathing loop (CSS keyframes)
 *  - "broken":      violent shake + 90% scale + grayscale (CSS keyframes)
 *  - "celebrating": dramatic bouncy entrance from below (framer-motion spring)
 *
 * Pure CSS animations are defined in /app/frontend/src/styles/guapo.css.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';

// Canonical Guapo mascot (also used on the About page)
export const GUAPO_IMAGE_URL =
  'https://customer-assets.emergentagent.com/job_gen-z-budgeting/artifacts/oab8ybbw_file_0000000021ac71f8b5eeb2af9d8946a7.png';

const SIZE_CLASSES = {
  xs: 'w-12 h-12',
  sm: 'w-16 h-16',
  md: 'w-24 h-24',
  lg: 'w-32 h-32',
  xl: 'w-40 h-40',
};

export function GuapoMascot({
  mode = 'idle',
  size = 'md',
  className = '',
  alt = 'Guapo',
  onTap,
  'data-testid': testId,
}) {
  const sizeClass = SIZE_CLASSES[size] || SIZE_CLASSES.md;
  const [winking, setWinking] = useState(false);

  const handleClick = (e) => {
    if (!onTap) return;
    if (winking) return; // prevent re-trigger mid-animation
    setWinking(true);
    // Match guapoWink keyframe duration
    setTimeout(() => setWinking(false), 700);
    onTap(e);
  };

  const interactiveProps = onTap
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: handleClick,
        onKeyDown: (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        },
        style: { cursor: 'pointer' },
      }
    : {};

  // Celebrating mode uses framer-motion for a single dramatic entrance,
  // while idle / broken loop forever via CSS keyframes (cheaper, smoother).
  if (mode === 'celebrating') {
    return (
      <motion.img
        src={GUAPO_IMAGE_URL}
        alt={alt}
        data-testid={testId || 'guapo-celebrating'}
        className={`${sizeClass} object-contain drop-shadow-2xl guapo-celebrating-loop ${className}`}
        initial={{ y: 600, scale: 0.4, rotate: -15, opacity: 0 }}
        animate={{ y: 0, scale: 1, rotate: 0, opacity: 1 }}
        exit={{ y: 200, scale: 0.6, opacity: 0 }}
        transition={{
          type: 'spring',
          stiffness: 240,
          damping: 12,
          mass: 1.1,
          delay: 0.15,
        }}
      />
    );
  }

  const baseAnimation = mode === 'broken' ? 'guapo-broken' : 'guapo-idle';
  const animationClass = winking ? `${baseAnimation} guapo-wink` : baseAnimation;

  return (
    <img
      src={GUAPO_IMAGE_URL}
      alt={alt}
      data-testid={testId || `guapo-${mode}`}
      className={`${sizeClass} object-contain ${animationClass} ${className}`}
      {...interactiveProps}
    />
  );
}

export default GuapoMascot;
